import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { setConfig } from "../../../store/config/config-slice";
import { addNotification } from "../../../store/notification-store";
import { v4 as uuidv4 } from "uuid";
import { SketchPicker } from "react-color";

/**
 * ColorPickerField Component
 * Un campo che combina un input di testo per il codice colore e un color picker
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Etichetta del campo
 * @param {string} props.value - Valore corrente del colore (hex)
 * @param {Function} props.onChange - Funzione da chiamare quando il colore cambia
 * @param {string} props.name - Nome del campo per gestire il cambio
 * @returns {JSX.Element} Campo colore con picker
 */
const ColorPickerField = ({ label, value, onChange, name }) => {
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [color, setColor] = useState(value || "#000000");
  const pickerRef = useRef(null);

  // Aggiorna il colore interno quando cambia il valore delle props
  useEffect(() => {
    setColor(value || "#000000");
  }, [value]);

  // Gestisce i click fuori dal color picker per chiuderlo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        displayColorPicker &&
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setDisplayColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [displayColorPicker]);

  // Toggle del color picker
  const handleClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };

  // Gestisce il cambio di colore dal picker
  const handleColorChange = (newColor) => {
    setColor(newColor.hex);

    // Simula un evento onChange per mantenere la compatibilità con il componente esistente
    const fakeEvent = {
      target: {
        name,
        value: newColor.hex,
      },
    };

    onChange(fakeEvent);
  };

  // Gestisce il cambio di input manuale
  const handleInputChange = (e) => {
    const newColor = e.target.value;

    // Verifica che sia un colore hex valido
    if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(newColor)) {
      setColor(newColor);
      onChange(e);
    } else if (newColor.startsWith("#") && newColor.length <= 7) {
      // Consenti la digitazione, ma non aggiornare il genitore finché non è un hex valido
      setColor(newColor);
    }
  };

  // Stili per il componente
  const styles = {
    color: {
      width: "36px",
      height: "36px",
      borderRadius: "4px",
      background: color,
      cursor: "pointer",
      border: "1px solid #ccc",
    },
    popover: {
      position: "absolute",
      zIndex: "10",
      marginTop: "2px",
    },
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-theme-primary">
        {label}
      </label>
      <div className="flex items-center">
        <div className="relative w-full flex items-center p-2 border border-theme-neutral rounded focus-within:ring-2 focus-within:ring-theme-accent">
          <input
            type="text"
            value={color}
            name={name}
            onChange={handleInputChange}
            className="flex-grow outline-none text-theme-primary"
            maxLength={7}
          />
          <div style={styles.color} onClick={handleClick} className="ml-2" />
          {displayColorPicker && (
            <div style={styles.popover} ref={pickerRef}>
              <SketchPicker
                color={color}
                onChange={handleColorChange}
                disableAlpha={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * CustomerConfigurations Component
 * Manages customer-specific configuration settings through a multi-section form
 *
 * Features:
 * - General information management
 * - Branding configuration
 * - Theme customization
 * - Image uploads (logo, banner, profile pic)
 * - Collapsible sections
 * - Form state management
 * - API integration
 *
 * @returns {JSX.Element} A form interface for managing customer configurations
 */
const CustomerConfigurations = () => {
  const config = useSelector((state) => state.config);
  const dispatch = useDispatch();

  /**
   * State for managing section visibility
   */
  const [openSections, setOpenSections] = useState({
    general: true,
    branding: true,
    themes: false,
    images: true, // Nuova sezione per le immagini
  });

  /**
   * State for managing individual theme expansion
   */
  const [openThemes, setOpenThemes] = useState({});

  /**
   * State for managing file uploads
   */
  const [uploadedFiles, setUploadedFiles] = useState({
    logoImg: null,
    bannerImg: null,
    profilePicImg: null,
  });

  /**
   * State for image previews
   */
  const [imagePreviews, setImagePreviews] = useState({
    logoImg: null,
    bannerImg: null,
    profilePicImg: null,
  });

  /**
   * Main form state containing all configuration data
   */
  const [formData, setFormData] = useState({
    appName: "",
    name: "",
    websiteUrl: "",
    address: "",
    contactEmail: "",
    phoneNumber: "",
    latitude: "",
    longitude: "",
    theming: {
      currentTheme: "light",
      themes: [],
    },
    brandingSchema: {
      footerText: "",
      urls: [],
      logoUrl: "",
      faviconUrl: "",
    },
    logoImgUrl: "",
    bannerImgUrl: "",
    profilePicImgUrl: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Effect hook to initialize form data from config
   */
  useEffect(() => {
    if (config?.clientConfig?.data) {
      setFormData(config.clientConfig.data);

      // Generate image previews from existing URLs
      if (config.clientConfig.data.logoImgUrl) {
        setImagePreviews((prev) => ({
          ...prev,
          logoImg: config.clientConfig.data.logoImgUrl,
        }));
      }

      if (config.clientConfig.data.bannerImgUrl) {
        setImagePreviews((prev) => ({
          ...prev,
          bannerImg: config.clientConfig.data.bannerImgUrl,
        }));
      }

      if (config.clientConfig.data.profilePicImgUrl) {
        setImagePreviews((prev) => ({
          ...prev,
          profilePicImg: config.clientConfig.data.profilePicImgUrl,
        }));
      }

      const initialThemeState = {};
      config.clientConfig.data.theming.themes.forEach((theme) => {
        initialThemeState[theme._id] = false;
      });
      setOpenThemes(initialThemeState);
    }
  }, [config]);

  /**
   * Handles image file selection
   * @param {Event} e - Input change event
   * @param {string} fieldName - Name of the image field
   */
  const handleImageChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Update the uploaded files state
    setUploadedFiles((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    // Create a preview URL
    const previewUrl = URL.createObjectURL(file);
    setImagePreviews((prev) => ({
      ...prev,
      [fieldName]: previewUrl,
    }));
  };

  /**
   * Removes an uploaded image
   * @param {string} fieldName - Name of the image field to clear
   */
  const removeImage = (fieldName) => {
    // Clear the file
    setUploadedFiles((prev) => ({
      ...prev,
      [fieldName]: null,
    }));

    // Clear the preview
    setImagePreviews((prev) => ({
      ...prev,
      [fieldName]: null,
    }));

    // Clear the stored URL if it exists
    if (fieldName === "logoImg") {
      setFormData((prev) => ({ ...prev, logoImgUrl: "" }));
    } else if (fieldName === "bannerImg") {
      setFormData((prev) => ({ ...prev, bannerImgUrl: "" }));
    } else if (fieldName === "profilePicImg") {
      setFormData((prev) => ({ ...prev, profilePicImgUrl: "" }));
    }
  };

  /**
   * Creates a new theme with default values
   */
  const createNewTheme = () => {
    const newTheme = {
      name: "",
      colors: {
        primary: "#000000",
        secondary: "#000000",
        accent: "#000000",
        neutral: "#000000",
      },
      bg: {
        primary: "#ffffff",
        secondary: "#ffffff",
        accent: "#000000",
        neutral: "#000000",
      },
      text: {
        primary: "#000000",
        secondary: "#000000",
        accent: "#000000",
        neutral: "#000000",
      },
      border: {
        primary: "#000000",
        secondary: "#000000",
        accent: "#000000",
        neutral: "#000000",
      },
      notifications: {
        success: {
          text: "#065F46",
          background: "#D1FAE5",
          border: "#34D399",
        },
        error: {
          text: "#991B1B",
          background: "#FEE2E2",
          border: "#F87171",
        },
        info: {
          text: "#1E40AF",
          background: "#DBEAFE",
          border: "#60A5FA",
        },
      },
    };

    setFormData((prev) => ({
      ...prev,
      theming: {
        ...prev.theming,
        themes: [...prev.theming.themes, newTheme],
      },
    }));

    setOpenThemes((prev) => ({
      ...prev,
      [newTheme._id]: true,
    }));
  };

  /**
   * Deletes a theme from the configuration
   * @param {string} themeId - ID of the theme to delete
   * @param {string} themeName - Name of the theme to delete
   */
  const deleteTheme = (themeId, themeName) => {
    setFormData((prev) => ({
      ...prev,
      theming: {
        ...prev.theming,
        themes: prev.theming.themes.filter((t) => t._id !== themeId),
        currentTheme:
          prev.theming.currentTheme === themeName
            ? prev.theming.themes.find((t) => t._id !== themeId)?.name ||
              "light"
            : prev.theming.currentTheme,
      },
    }));

    setOpenThemes((prev) => {
      const newState = { ...prev };
      delete newState[themeId];
      return newState;
    });
  };

  /**
   * Prepares form data including images for submission
   * @returns {FormData} FormData object with all form data and files
   */
  const prepareFormDataWithImages = () => {
    const formDataToSubmit = new FormData();

    // Add all the regular form data as JSON
    formDataToSubmit.append("data", JSON.stringify(formData));

    // Add file uploads if they exist
    if (uploadedFiles.logoImg) {
      formDataToSubmit.append("logoImg", uploadedFiles.logoImg);
    }

    if (uploadedFiles.bannerImg) {
      formDataToSubmit.append("bannerImg", uploadedFiles.bannerImg);
    }

    if (uploadedFiles.profilePicImg) {
      formDataToSubmit.append("profilePicImg", uploadedFiles.profilePicImg);
    }

    return formDataToSubmit;
  };

  /**
   * Handles form submission and API interaction
   * @param {Event} e - Form submission event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data with file uploads
      const formDataToSubmit = prepareFormDataWithImages();

      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/customer-configurations/${formData._id}`,
        formDataToSubmit,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data) {
        const newNotification = {
          id: uuidv4(),
          message: "Configurazione aggiornata con successo.",
          type: "success",
        };
        dispatch(addNotification(newNotification));
        dispatch(setConfig(response.data));

        // Reset file upload states after successful submission
        setUploadedFiles({
          logoImg: null,
          bannerImg: null,
          profilePicImg: null,
        });
      }
    } catch (err) {
      console.log(err);
      const newNotification = {
        id: uuidv4(),
        message: "Errore durante l'aggiornamento della configurazione.",
        type: "error",
      };
      dispatch(addNotification(newNotification));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles form input changes across different sections
   * @param {Event} e - Input change event
   * @param {string} section - Form section identifier
   * @param {number} themeIndex - Index of theme being modified
   * @param {string} category - Category within theme being modified
   */
  const handleChange = (
    e,
    section = null,
    themeIndex = null,
    category = null,
  ) => {
    const { name, value } = e.target;

    if (section === "theme" && category) {
      const updatedThemes = [...formData.theming.themes];
      const theme = { ...updatedThemes[themeIndex] };

      if (category === "notifications") {
        const [notifType, notifProp] = name.split(".");
        theme.notifications = {
          ...theme.notifications,
          [notifType]: {
            ...theme.notifications[notifType],
            [notifProp]: value,
          },
        };
      } else {
        theme[category] = {
          ...theme[category],
          [name]: value,
        };
      }

      updatedThemes[themeIndex] = theme;

      setFormData((prev) => ({
        ...prev,
        theming: {
          ...prev.theming,
          themes: updatedThemes,
        },
      }));
    } else if (section === "theming" && name === "currentTheme") {
      setFormData((prev) => ({
        ...prev,
        theming: {
          ...prev.theming,
          currentTheme: value,
        },
      }));
    } else if (section === "branding") {
      setFormData((prev) => ({
        ...prev,
        brandingSchema: {
          ...prev.brandingSchema,
          [name]: value,
        },
      }));
    } else if (section === "social") {
      const updatedUrls = [...formData.brandingSchema.urls];
      updatedUrls[themeIndex] = {
        ...updatedUrls[themeIndex],
        [name]: value,
      };
      setFormData((prev) => ({
        ...prev,
        brandingSchema: {
          ...prev.brandingSchema,
          urls: updatedUrls,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  /**
   * Toggles visibility of main form sections
   * @param {string} section - Section identifier
   */
  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  /**
   * Toggles visibility of individual themes
   * @param {string} themeId - Theme identifier
   */
  const toggleTheme = (themeId) => {
    setOpenThemes((prev) => ({
      ...prev,
      [themeId]: !prev[themeId],
    }));
  };

  /**
   * ColorSection Component
   * Renders a section of color inputs for theme customization
   *
   * @param {Object} props - Component props
   * @param {string} props.title - Section title
   * @param {string} props.category - Color category
   * @param {Object} props.theme - Theme data
   * @param {number} props.themeIndex - Theme index
   * @param {Object} props.values - Color values
   * @returns {JSX.Element} A section of color inputs
   */
  const ColorSection = ({ title, category, theme, themeIndex, values }) => {
    if (category === "notifications") {
      return (
        <div className="mb-6">
          <h3 className="font-medium text-lg mb-3 capitalize text-theme-primary">
            {title}
          </h3>
          {Object.entries(values).map(([type, colors]) => (
            <div key={type} className="mb-4">
              <h4 className="font-medium mb-2 capitalize text-theme-primary">
                {type}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(colors).map(([key, value]) => (
                  <div key={`${type}-${key}`}>
                    <ColorPickerField
                      label={key.charAt(0).toUpperCase() + key.slice(1)}
                      name={`${type}.${key}`}
                      value={value}
                      onChange={(e) =>
                        handleChange(e, "theme", themeIndex, category)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mb-6">
        <h3 className="font-medium text-lg mb-3 capitalize text-theme-primary">
          {title}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(values).map(([key, value]) => (
            <div key={`${category}-${key}`}>
              <ColorPickerField
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                name={key}
                value={value}
                onChange={(e) => handleChange(e, "theme", themeIndex, category)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * ImageUploader Component
   * Renders an image upload field with preview
   *
   * @param {Object} props - Component props
   * @param {string} props.label - Field label
   * @param {string} props.fieldName - Field identifier
   * @param {string} props.preview - Preview URL
   * @param {Function} props.onChange - Change handler
   * @param {Function} props.onRemove - Remove handler
   * @returns {JSX.Element} An image upload field with preview
   */
  const ImageUploader = ({ label, fieldName, preview, onChange, onRemove }) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1 text-theme-primary">
          {label}
        </label>
        <div className="flex flex-col space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onChange(e, fieldName)}
            className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent"
          />

          {preview && (
            <div className="relative mt-2">
              <img
                src={preview}
                alt={`Preview di ${label}`}
                className="max-w-xs max-h-40 object-contain border border-theme-neutral rounded"
              />
              <button
                type="button"
                onClick={() => onRemove(fieldName)}
                className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                title="Rimuovi immagine"
              >
                ×
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-6 space-y-8">
      {/* General Information Accordion */}
      <div className="border border-theme-neutral rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection("general")}
          className="w-full px-4 py-3 text-left font-semibold flex justify-between items-center border-b border-theme-neutral bg-theme-neutral text-theme-primary"
        >
          <span>Informazioni Generali</span>
          <span className="transform transition-transform duration-200">
            {openSections.general ? "−" : "+"}
          </span>
        </button>
        <div
          className={`transition-all duration-200 ${
            openSections.general ? "p-4" : "hidden"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Nome App
              </label>
              <input
                type="text"
                name="appName"
                value={formData.appName}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Nome
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                URL Sito Web
              </label>
              <input
                type="url"
                name="websiteUrl"
                value={formData.websiteUrl}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Indirizzo
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Email
              </label>
              <input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Telefono
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Latitudine
              </label>
              <input
                type="number"
                name="latitude"
                value={formData.latitude}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Longitudine
              </label>
              <input
                type="number"
                name="longitude"
                value={formData.longitude}
                onChange={handleChange}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
          </div>
        </div>
      </div>

      {/* Images Accordion */}
      <div className="border border-theme-neutral rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection("images")}
          className="w-full px-4 py-3 text-left font-semibold flex justify-between items-center border-b border-theme-neutral bg-theme-neutral text-theme-primary"
        >
          <span>Immagini</span>
          <span className="transform transition-transform duration-200">
            {openSections.images ? "−" : "+"}
          </span>
        </button>
        <div
          className={`transition-all duration-200 ${
            openSections.images ? "p-4" : "hidden"
          }`}
        >
          <div className="space-y-4">
            <ImageUploader
              label="Logo"
              fieldName="logoImg"
              preview={imagePreviews.logoImg}
              onChange={handleImageChange}
              onRemove={removeImage}
            />

            <ImageUploader
              label="Banner"
              fieldName="bannerImg"
              preview={imagePreviews.bannerImg}
              onChange={handleImageChange}
              onRemove={removeImage}
            />

            <ImageUploader
              label="Immagine Profilo"
              fieldName="profilePicImg"
              preview={imagePreviews.profilePicImg}
              onChange={handleImageChange}
              onRemove={removeImage}
            />
          </div>
        </div>
      </div>

      {/* Branding Accordion */}
      <div className="border border-theme-neutral rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection("branding")}
          className="w-full px-4 py-3 text-left font-semibold flex justify-between items-center border-b border-theme-neutral bg-theme-neutral text-theme-primary"
        >
          <span>Branding</span>
          <span className="transform transition-transform duration-200">
            {openSections.branding ? "−" : "+"}
          </span>
        </button>
        <div
          className={`transition-all duration-200 ${
            openSections.branding ? "p-4" : "hidden"
          }`}
        >
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                Testo Footer
              </label>
              <input
                type="text"
                name="footerText"
                value={formData.brandingSchema.footerText}
                onChange={(e) => handleChange(e, "branding")}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                URL Logo
              </label>
              <input
                type="url"
                name="logoUrl"
                value={formData.brandingSchema.logoUrl}
                onChange={(e) => handleChange(e, "branding")}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-theme-primary">
                URL Favicon
              </label>
              <input
                type="url"
                name="faviconUrl"
                value={formData.brandingSchema.faviconUrl}
                onChange={(e) => handleChange(e, "branding")}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
              />
            </div>
            <div className="space-y-4">
              <h3 className="font-medium text-theme-primary">
                Social Media URLs
              </h3>
              {formData.brandingSchema.urls.map((social, index) => (
                <div
                  key={social._id}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                >
                  <div>
                    <label className="block text-sm font-medium mb-1 text-theme-primary">
                      URL
                    </label>
                    <input
                      type="url"
                      name="url"
                      value={social.url}
                      onChange={(e) => handleChange(e, "social", index)}
                      className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-theme-primary">
                      Etichetta Icona
                    </label>
                    <input
                      type="text"
                      name="iconLabel"
                      value={social.iconLabel}
                      onChange={(e) => handleChange(e, "social", index)}
                      className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-theme-primary">
                      Colore Icona
                    </label>
                    <ColorPickerField
                      label=""
                      name="iconColor"
                      value={social.iconColor}
                      onChange={(e) => handleChange(e, "social", index)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Themes Accordion */}
      <div className="border border-theme-neutral rounded-lg">
        <button
          type="button"
          onClick={() => toggleSection("themes")}
          className="w-full px-4 py-3 text-left font-semibold flex justify-between items-center border-b border-theme-neutral bg-theme-neutral text-theme-primary"
        >
          <span>Temi</span>
          <span className="transform transition-transform duration-200">
            {openSections.themes ? "−" : "+"}
          </span>
        </button>
        <div
          className={`transition-all duration-200 ${
            openSections.themes ? "p-4" : "hidden"
          }`}
        >
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-theme-primary">
                Tema Corrente
              </label>
              <button
                type="button"
                onClick={createNewTheme}
                className="px-4 py-2 bg-theme-accent text-white rounded hover:opacity-90"
              >
                Aggiungi Nuovo Tema
              </button>
            </div>

            <div>
              <select
                name="currentTheme"
                value={formData.theming.currentTheme}
                onChange={(e) => handleChange(e, "theming")}
                className="w-full p-2 border border-theme-neutral rounded focus:ring-2 focus:ring-theme-accent text-theme-primary bg-white"
                required
              >
                {formData.theming.themes.map((theme) => (
                  <option key={theme._id} value={theme.name}>
                    {theme.name}
                  </option>
                ))}
              </select>
            </div>

            {formData.theming.themes.map((theme, index) => (
              <div
                key={theme._id}
                className="border border-theme-neutral rounded"
              >
                <div className="w-full px-4 py-2 text-left font-medium flex justify-between items-center capitalize text-theme-primary bg-theme-neutral">
                  <div className="flex items-center space-x-2 flex-1">
                    <input
                      type="text"
                      value={theme.name}
                      onChange={(e) => {
                        const updatedThemes = [...formData.theming.themes];
                        updatedThemes[index] = {
                          ...theme,
                          name: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          theming: {
                            ...prev.theming,
                            themes: updatedThemes,
                          },
                        }));
                      }}
                      className="px-2 py-1 border border-theme-neutral rounded flex-1"
                      placeholder="Nome tema"
                    />
                    <button
                      type="button"
                      onClick={() => deleteTheme(theme._id, theme.name)}
                      className="ml-2 px-2 py-1 text-red-500 hover:text-red-700"
                    >
                      Elimina
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleTheme(theme._id)}
                    className="ml-4"
                  >
                    <span className="transform transition-transform duration-200">
                      {openThemes[theme._id] ? "−" : "+"}
                    </span>
                  </button>
                </div>
                <div
                  className={`transition-all duration-200 ${
                    openThemes[theme._id] ? "p-4" : "hidden"
                  }`}
                >
                  <ColorSection
                    title="Colori di Base"
                    category="colors"
                    theme={theme}
                    themeIndex={index}
                    values={theme.colors}
                  />
                  <ColorSection
                    title="Colori di Sfondo"
                    category="bg"
                    theme={theme}
                    themeIndex={index}
                    values={theme.bg}
                  />
                  <ColorSection
                    title="Colori del Testo"
                    category="text"
                    theme={theme}
                    themeIndex={index}
                    values={theme.text}
                  />
                  <ColorSection
                    title="Colori dei Bordi"
                    category="border"
                    theme={theme}
                    themeIndex={index}
                    values={theme.border}
                  />
                  {theme.notifications && (
                    <ColorSection
                      title="Colori Notifiche"
                      category="notifications"
                      theme={theme}
                      themeIndex={index}
                      values={theme.notifications}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`px-4 py-2 bg-theme-accent text-white rounded hover:opacity-90 focus:ring-2 focus:ring-theme-accent 
            ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isSubmitting ? "Salvataggio..." : "Salva Configurazione"}
        </button>
      </div>
    </form>
  );
};

export default CustomerConfigurations;
